import React, {Fragment, type ReactElement, useEffect, useState} from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ShareDocumentModal} from 'app/general/modals/ShareDocumentModal/ShareDocumentModal';
import {DocumentMetadataStatusButton} from 'app/general/structures/DocumentMetadataStatusButton';
import {useUpdateProcedure} from 'app/procedures/api';
import {sweetAPI} from 'app/store/sweet-api';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {PROCESS_RTK_TAGS} from 'app/utils/rtk-tags';

import type {MinimumActionableType} from 'app/general/modals/ShareDocumentModal/types';
import type {ShareableContentType} from 'app/user/types';
import type {
  ContentType,
  RegularProcessProcedureAsStepSerializedType,
} from 'sweet-types';

type TooltipProps = {
  ct: ContentType;
  isPrivate: boolean;
  children: ReactElement;
};
export const DocumentPrivateStatusTooltip = ({
  isPrivate,
  ct,
  children,
}: TooltipProps) => (
  <LazyTooltip
    placement="top"
    tooltip={
      isPrivate
        ? gt(
            'This %s is private and can be accessed only by teammates with proper permission',
            getContentType(ct).name
          )
        : gt(
            'This %s is public and can be accessed by anyone',
            getContentType(ct).name
          )
    }
  >
    {children}
  </LazyTooltip>
);

type ButtonProps<T extends MinimumActionableType> = {
  ct: ShareableContentType;
  isPrivate: boolean;
  canEdit: boolean;
  object: T;
  actionCallback?: (minimalCallbackObject: {
    url: string;
    isPrivate: boolean;
  }) => void;
};

export function DocumentPrivateStatusButton<T extends MinimumActionableType>({
  object,
  ct,
  isPrivate,
  canEdit,
  actionCallback,
}: ButtonProps<T>) {
  const [visible, setVisible] = useState(false);

  return (
    <Fragment>
      <ShareDocumentModal
        show={visible}
        object={object}
        contentType={ct}
        onClose={() => setVisible(false)}
        hideTeammateTab={true}
        hidePublicTab={false}
        initiallySelectedTab="show-public"
        actionCallback={actionCallback}
      />
      <DocumentPrivateStatusTooltip isPrivate={isPrivate} ct={ct}>
        <DocumentMetadataStatusButton
          disabled={!canEdit}
          active={!isPrivate}
          onClick={() => setVisible(true)}
        >
          <i className="fa fa-fw fa-globe-americas" />
        </DocumentMetadataStatusButton>
      </DocumentPrivateStatusTooltip>
    </Fragment>
  );
}

type ProcedureStepPrivateStatusProps = {
  step: RegularProcessProcedureAsStepSerializedType;
  processURL?: string;
};

export function ProcedureStepPrivateStatus({
  step,
  processURL,
}: ProcedureStepPrivateStatusProps) {
  const [procedureFromStep, setProcedureFromStep] =
    useState<MinimumActionableType>({
      id: step.procedure_id,
      url: step.url,
      html_url: step.html_url,
      name: step.name,
      content_type: 'procedure',
      private: step.private,
      hashid: step.hashid,
      embed_url: step.embed_url,
    });

  useEffect(() => {
    setProcedureFromStep({
      id: step.procedure_id,
      url: step.url,
      html_url: step.html_url,
      name: step.name,
      content_type: 'procedure',
      private: step.private,
      hashid: step.hashid,
      embed_url: step.embed_url,
    });
  }, [step.private]);

  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });
  const [updateProcedure, {isSuccess}] = useUpdateProcedure();

  useEffect(() => {
    if (isSuccess) {
      actions.invalidateTags([
        // @ts-expect-error we inject tags on the API after created ts doesn't know them.
        {type: PROCESS_RTK_TAGS.TAGS, id: processURL},
        // @ts-expect-error we inject tags on the API after created ts doesn't know them.
        {type: PROCESS_RTK_TAGS.HASHID, id: processURL},
      ]);
    }
  }, [isSuccess]);

  const setProcedurePrivate = ({url, isPrivate}) =>
    updateProcedure({
      url,
      private: isPrivate,
      successMessage: isPrivate
        ? gt('This procedure is now private')
        : gt('This procedure is now public'),
    });

  return (
    <DocumentPrivateStatusButton
      object={procedureFromStep}
      ct="procedure"
      isPrivate={step.private}
      canEdit={step.can_edit}
      actionCallback={setProcedurePrivate}
    />
  );
}

type IconProps = {
  ct: ContentType;
  isPrivate: boolean;
  className?: string;
};
export const DocumentPrivateStatusIcon = ({
  ct,
  className,
  isPrivate,
}: IconProps) =>
  !isPrivate ? (
    <DocumentPrivateStatusTooltip isPrivate={isPrivate} ct={ct}>
      <span className={cs(className)}>
        <i className="fa fa-globe-americas" />
      </span>
    </DocumentPrivateStatusTooltip>
  ) : null;
